import type { ErrorDto, TradeRequestDto } from '@utility-nyc/react-query-sdk';

import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { AppStateStatus } from 'react-native';
import { AppState } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { Client } from '@twilio/conversations';
import { useAuthenticationStatus } from '@utility-nyc/react-auth-core';
import { useAuthStore } from '@utility-nyc/react-auth-storage';
import { useShallow } from 'zustand/react/shallow';

import { Toast } from '@shared/utils';
import { useRefreshTwilioAccessToken } from '@south-street-app/hooks';
import {
  MessageType,
  useBackendError,
  useChatStore,
  useGlobalBottomSheetStore,
  useMobileTradeStore,
} from '@south-street-app/stores';
import {
  TradeRequestDtoStatus,
  createOrGetSupport,
  getOrCreateBroadcast,
  useEndUserAccessToken,
  useGetCurrentEndUser,
  useGetInitialEndUserState,
} from '@utility-nyc/react-query-sdk';

import {
  BROADCAST_UPDATES_UNIQUE_NAME,
  SUPPORT_UNIQUE_NAME_PREFIX,
  isTradeConversation,
  refreshUnreadCounts,
  sortArchivedConversations,
} from '../chatV2';

type TwilioWrapperProps = {
  children: ReactNode;
};

const TRADE_COMPLETED_STATUSES: TradeRequestDtoStatus[] = [
  TradeRequestDtoStatus.EXECUTED,
  TradeRequestDtoStatus.CANCELED,
  TradeRequestDtoStatus.OFFER_REJECTED,
];

const getConversationReference = (conversationData: unknown) => {
  if (!conversationData || typeof conversationData !== 'object') {
    return null;
  }

  const conversationRecord = conversationData as Record<string, unknown>;
  const conversationSid = Reflect.get(conversationRecord, 'sid') as unknown;
  const conversationUniqueName = Reflect.get(
    conversationRecord,
    'uniqueName',
  ) as unknown;

  if (typeof conversationSid === 'string') {
    return conversationSid;
  }

  if (typeof conversationUniqueName === 'string') {
    return conversationUniqueName;
  }

  return null;
};

const getConversationByReference = async (
  twilioClient: Client,
  reference: string,
) => {
  try {
    return await twilioClient.getConversationByUniqueName(reference);
  } catch {
    return twilioClient.getConversationBySid(reference);
  }
};

const formatErrorDto = (error: ErrorDto): string => {
  const contextMessage =
    error.context &&
    typeof error.context === 'object' &&
    'message' in error.context &&
    typeof error.context.message === 'string'
      ? error.context.message
      : null;

  return [error.message, contextMessage].filter(Boolean).join(': ');
};

const TwilioWrapper = ({ children }: TwilioWrapperProps) => {
  const openBottomSheet = useGlobalBottomSheetStore(
    (globalState) => globalState.openBottomSheet,
  );

  const navigation = useNavigation();
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const handleLogout = useAuthStore((state) => state.handleLogout);
  const setBackendError = useBackendError((state) => state.setBackendErrorData);
  const resetBackendError = useBackendError(
    (state) => state.resetBackendErrorData,
  );

  const {
    chatConversation,
    getTradeRequestId,
    setConversation,
    setTradeProposalId,
    setCounterProposal,
    setReceivedQuotedPrice,
    setTradeTimeout,
    setProposal,
    setChatConversation,
    setChatAuthor,
    setChatTrader,
    setEndUserId,
    setTradeRequestId,
    setIsNewQuotedPrice,
    setIsTwilioConnected,
  } = useMobileTradeStore(
    useShallow((state) => ({
      setIsTwilioConnected: state.setIsTwilioConnected,
      chatConversation: state.chatConversation,
      getTradeRequestId: state.getTradeRequestId,
      setConversation: state.setConversation,
      setTradeProposalId: state.setTradeProposalId,
      setCounterProposal: state.setCounterProposal,
      setReceivedQuotedPrice: state.setReceivedQuotedPrice,
      setTradeTimeout: state.setTradeTimeout,
      setProposal: state.setProposal,
      setChatConversation: state.setChatConversation,
      setChatAuthor: state.setChatAuthor,
      setChatTrader: state.setChatTrader,
      setEndUserId: state.setEndUserId,
      setTradeRequestId: state.setTradeRequestId,
      setIsNewQuotedPrice: state.setIsNewQuotedPrice,
    })),
  );
  const { data } = useGetInitialEndUserState({
    query: { enabled: !!refreshToken },
  });

  const {
    data: accessTokens,
    refetch: refetchAccessTokens,
    error: accessTokensError,
  } = useEndUserAccessToken({ query: { enabled: !!refreshToken } });
  const { data: currentUser } = useGetCurrentEndUser({
    query: { enabled: !!refreshToken },
  });
  const {
    setArchivedConversations,
    setLockChat,
    setSupportConversation,
    setUpdatesConversation,
    bumpInboxRefreshKey,
    resetChatState,
  } = useChatStore(
    useShallow((state) => ({
      setSupportConversation: state.setSupportConversation,
      setUpdatesConversation: state.setUpdatesConversation,
      setArchivedConversations: state.setArchivedConversations,
      setLockChat: state.setLockChat,
      bumpInboxRefreshKey: state.bumpInboxRefreshKey,
      resetChatState: state.resetChatState,
    })),
  );

  const authStatus = useAuthenticationStatus();

  const updateConversation = useCallback(
    async (twilioClient: Client) => {
      if (chatConversation && chatConversation.uniqueName) {
        const newChatConverstation =
          await twilioClient.getConversationByUniqueName(
            chatConversation.uniqueName,
          );

        setChatConversation(newChatConverstation);
      }
    },
    [chatConversation, setChatConversation],
  );

  const [client, setClient] = useState<Client | undefined>();
  const hydrateChatConversationsPromiseRef = useRef<Promise<void> | null>(null);

  const hydrateArchivedConversations = useCallback(
    async (twilioClient: Client) => {
      const archivedConversations = [];
      let paginator = await twilioClient.getSubscribedConversations();
      let hasNextPage = true;

      while (hasNextPage) {
        archivedConversations.push(
          ...paginator.items.filter((conversation) =>
            isTradeConversation(conversation),
          ),
        );

        hasNextPage = paginator.hasNextPage;

        if (hasNextPage) {
          paginator = await paginator.nextPage();
        }
      }

      setArchivedConversations(sortArchivedConversations(archivedConversations));
    },
    [setArchivedConversations],
  );

  const hydrateChatConversations = useCallback(
    async (twilioClient: Client) => {
      if (hydrateChatConversationsPromiseRef.current) {
        return hydrateChatConversationsPromiseRef.current;
      }

      const hydratePromise = (async () => {
        const endUserId = currentUser?.data.id;

        if (endUserId) {
          try {
            const supportResponse = await createOrGetSupport(endUserId);
            const supportReference = getConversationReference(
              supportResponse.data,
            );
            const supportConversation = supportReference
              ? await getConversationByReference(twilioClient, supportReference)
              : await twilioClient.getConversationByUniqueName(
                  `${SUPPORT_UNIQUE_NAME_PREFIX}${endUserId}`,
                );

            setSupportConversation(supportConversation);
          } catch {
            setSupportConversation(null);
          }
        }

        try {
          const updatesResponse = await getOrCreateBroadcast();
          const updatesReference = getConversationReference(updatesResponse.data);
          const updatesConversation = updatesReference
            ? await getConversationByReference(twilioClient, updatesReference)
            : await twilioClient.getConversationByUniqueName(
                BROADCAST_UPDATES_UNIQUE_NAME,
              );

          setUpdatesConversation(updatesConversation);
        } catch {
          setUpdatesConversation(null);
        }

        await hydrateArchivedConversations(twilioClient);

        await refreshUnreadCounts();
      })();

      hydrateChatConversationsPromiseRef.current = hydratePromise;

      try {
        await hydratePromise;
      } finally {
        hydrateChatConversationsPromiseRef.current = null;
      }
    },
    [
      currentUser?.data.id,
      hydrateArchivedConversations,
      setSupportConversation,
      setUpdatesConversation,
    ],
  );

  const updateClient = useCallback(async () => {
    if (!accessTokens?.data.twilioAccessToken) {
      return undefined;
    }

    if (client) {
      const newClient = await client.updateToken(
        accessTokens.data.twilioAccessToken,
      );

      await updateConversation(newClient);

      await hydrateChatConversations(newClient);

      return newClient;
    }

    const newClient = new Client(accessTokens.data.twilioAccessToken);

    setClient(newClient);

    return newClient;
  }, [
    accessTokens?.data.twilioAccessToken,
    client,
    hydrateChatConversations,
    updateConversation,
  ]);

  useRefreshTwilioAccessToken();

  const clearTwilioAccessTokenIfLoggedOut = useCallback(() => {
    if (authStatus === 'logged out' && accessTokens?.data.twilioAccessToken) {
      useAuthStore.getState().twilioAccessToken = undefined;

      resetChatState();
    }
  }, [accessTokens?.data.twilioAccessToken, authStatus, resetChatState]);

  const offerRejectedCheck = useCallback(
    async (messageData: TradeRequestDto, twilioClient: Client) => {
      if (
        messageData.status === TradeRequestDtoStatus.OFFER_REJECTED &&
        messageData.chatTopicId
      ) {
        const chatConverstation =
          await twilioClient.getConversationByUniqueName(
            messageData.chatTopicId,
          );

        setChatConversation(chatConverstation);
      }
    },
    [setChatConversation],
  );
  const tradeRequestTimedoutCheck = useCallback(
    async (messageData: TradeRequestDto, twilioClient: Client) => {
      if (
        getTradeRequestId() === messageData.id &&
        messageData.status === TradeRequestDtoStatus.TIMED_OUT &&
        messageData.chatTopicId
      ) {
        setChatTrader(messageData.assignedTo?.firstName ?? 'Trader');

        setChatAuthor(`END_USER:${messageData.createdBy.id}`);

        openBottomSheet({
          type: 'tradeTimeout',
          subType: 'timeout',
        });

        const chatConverstation =
          await twilioClient.getConversationByUniqueName(
            messageData.chatTopicId,
          );

        setChatConversation(chatConverstation);
      }
    },
    [
      getTradeRequestId,
      openBottomSheet,
      setChatAuthor,
      setChatConversation,
      setChatTrader,
    ],
  );

  const tradeRequestCanceledCheck = useCallback(
    async (
      messageData: TradeRequestDto,
      messageType: MessageType,
      twilioClient: Client,
    ) => {
      if (
        messageType === MessageType.TradeRequestCanceled &&
        getTradeRequestId() === messageData.id &&
        messageData.chatTopicId
      ) {
        setChatTrader(messageData.assignedTo?.firstName ?? 'Trader');

        setChatAuthor(`END_USER:${messageData.createdBy.id}`);

        openBottomSheet({
          type: 'tradeTimeout',
          subType: 'canceled',
        });

        const chatConverstation =
          await twilioClient.getConversationByUniqueName(
            messageData.chatTopicId,
          );

        setChatConversation(chatConverstation);
      }
    },
    [
      getTradeRequestId,
      setChatTrader,
      setChatAuthor,
      openBottomSheet,
      setChatConversation,
    ],
  );

  const tradeRequestStateChangedCheck = useCallback(
    async (messageData: TradeRequestDto, type: MessageType) => {
      if (
        type === MessageType.TradeRequestStateChanged &&
        (messageData.status === 'OFFER_SENT' ||
          messageData.status === 'OFFER_RESENT') &&
        messageData.counterproposal?.id
      ) {
        if (messageData.status === 'OFFER_RESENT') {
          setIsNewQuotedPrice(true);
        }

        setTradeProposalId(messageData.counterproposal.id);

        setEndUserId(messageData.createdBy.id);

        setCounterProposal(messageData.counterproposal);

        setReceivedQuotedPrice(true);

        setChatAuthor(`END_USER:${messageData.createdBy.id}`);

        setChatTrader(messageData.assignedTo?.firstName || 'Trader');
      }
    },
    [
      setChatAuthor,
      setChatTrader,
      setCounterProposal,
      setEndUserId,
      setIsNewQuotedPrice,
      setReceivedQuotedPrice,
      setTradeProposalId,
    ],
  );

  const tradeRequestCreatedCheck = useCallback(
    async (messageData: TradeRequestDto, type: MessageType) => {
      if (type === MessageType.TradeRequestCreated) {
        setProposal(messageData.proposal);

        setTradeRequestId(messageData.id);

        resetBackendError();

        openBottomSheet({
          type: 'requestTrade',
        });
      }
    },
    [openBottomSheet, resetBackendError, setProposal, setTradeRequestId],
  );

  const tradeRequestExecuted = useCallback(
    async (messageData: TradeRequestDto, type: MessageType) => {
      if (
        type === MessageType.TradeRequestExecuted &&
        messageData.status === 'EXECUTED'
      ) {
        openBottomSheet({
          type: 'tradeConfirmation',
        });
      }
    },
    [openBottomSheet],
  );

  const onMessageAdded = useCallback(
    async (body: string | null, twilioClient: Client) => {
      const {
        type,
        data: messageData,
        success,
        error,
      } = JSON.parse(body || '') as
        | {
            type: MessageType;
            data: TradeRequestDto;
            error: undefined;
            success: true;
          }
        | {
            type: undefined;
            data: undefined;
            error: ErrorDto;
            success: false | undefined;
          };

      if (success) {
        await tradeRequestStateChangedCheck(messageData, type);

        await tradeRequestExecuted(messageData, type);

        await tradeRequestCreatedCheck(messageData, type);

        await tradeRequestCanceledCheck(messageData, type, twilioClient);

        await offerRejectedCheck(messageData, twilioClient);

        await tradeRequestTimedoutCheck(messageData, twilioClient);

        console.log('messageAdded', type, messageData);

        Toast.showDebugToast({
          visibilityTime: 5000,
          text1: `Twilio: ${type}`,
          text2: `status: ${messageData.status} id: ${messageData.id} ${messageData.createdBy.firstName} ${messageData.createdBy.lastName}`,
        });
      } else {
        if (success === false) {
          const formattedError = formatErrorDto(error);

          Sentry.captureException(error, {
            data: { message: 'request creation failed' },
          });

          setBackendError({ error: formattedError });

          openBottomSheet({
            type: 'backendError',
          });

          Toast.showDebugToast({
            visibilityTime: 5000,

            text1: `Twilio message error: ${formattedError}`,
          });
        }
      }
    },
    [
      tradeRequestStateChangedCheck,
      tradeRequestExecuted,
      tradeRequestCreatedCheck,
      tradeRequestCanceledCheck,
      offerRejectedCheck,
      tradeRequestTimedoutCheck,
      setBackendError,
      openBottomSheet,
    ],
  );

  const tradeInProgressCheck = useCallback(() => {
    const state = navigation.getState();
    const name = state && state.index ? state.routes[state.index].name : '';

    if (
      data?.data.tradeRequest?.status &&
      !TRADE_COMPLETED_STATUSES.includes(data.data.tradeRequest.status) &&
      name === 'TradingScreen'
    ) {
      openBottomSheet({
        type: 'tradeInProgress',
      });
    }
  }, [data, navigation, openBottomSheet]);

  const refreshTwilioAccessToken = useCallback(async () => {
    try {
      await refetchAccessTokens();
    } catch {
      // Token refresh failures are retryable; lockChat is driven by initial-state polling
    }
  }, [refetchAccessTokens]);

  const resetOnUnmount = useCallback(() => {
    client?.removeAllListeners();

    client?.shutdown();
  }, [client]);

  const [appState, setAppState] = useState(AppState.currentState);

  const handleAppStateChange = useCallback(
    async (nextAppState: AppStateStatus) => {
      if (appState.match(/inactive|background/) && nextAppState === 'active') {
        if (client?.connectionState !== 'connected') {
          setIsTwilioConnected(false);

          await updateClient();
        }
      }

      if (client?.connectionState === 'connected') {
        setIsTwilioConnected(true);
      }

      setAppState(nextAppState);
    },

    [appState, updateClient, client, setIsTwilioConnected],
  );

  useEffect(() => {
    if (client?.connectionState !== 'connected') {
      setIsTwilioConnected(false);
    }

    if (client?.connectionState === 'connected') {
      setIsTwilioConnected(true);
    }
  }, [client, setIsTwilioConnected]);

  useEffect(() => {
    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange,
    );

    return () => {
      subscription.remove();
    };
  }, [handleAppStateChange]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (accessTokensError?.response?.status === 401) {
      handleLogout();
    }
  }, [accessTokensError, handleLogout]);

  useEffect(() => {
    if (!client || !refreshToken) {
      return;
    }

    const handleInitialized = async () => {
      if (data?.data.liveUpdatesTopicId) {
        const conversation = await client.getConversationByUniqueName(
          data.data.liveUpdatesTopicId || '',
        );

        Toast.showDebugToast({
          text1: 'Twilio initialized',
        });

        setIsTwilioConnected(true);

        setConversation(conversation);

        setTradeTimeout(data.data.systemSettings.timeoutInSeconds);
      }

      await hydrateChatConversations(client);

      bumpInboxRefreshKey();
    };

    client.on('initialized', handleInitialized);

    if (client.connectionState === 'connected') {
      handleInitialized().catch(() => undefined);
    }

    return () => {
      client.removeListener('initialized', handleInitialized);
    };
  }, [
    bumpInboxRefreshKey,
    client,
    data?.data.liveUpdatesTopicId,
    data?.data.systemSettings.timeoutInSeconds,
    hydrateChatConversations,
    refreshToken,
    setConversation,
    setIsTwilioConnected,
    setTradeTimeout,
  ]);

  useEffect(() => {
    if (!client || !refreshToken) {
      return;
    }

    const handleInitFailed = (error: {
      error?: {
        message?: string;
      };
    }) => {
      console.error('Twilio initialization failed', error);

      Toast.showDebugToast({
        visibilityTime: 5000,
        text1: 'Twilio initialization failed',
        text2: error.error?.message,
      });

      openBottomSheet({
        type: 'tradeInProgress',
      });
    };

    client.on('initFailed', handleInitFailed);

    return () => {
      client.removeListener('initFailed', handleInitFailed);
    };
  }, [client, openBottomSheet, refreshToken]);

  useEffect(() => {
    if (!client || !refreshToken) {
      return;
    }

    const handleTokenExpired = async () => {
      await refreshTwilioAccessToken();
    };

    const handleTokenAboutToExpire = async () => {
      await refreshTwilioAccessToken();
    };

    client.on('tokenExpired', handleTokenExpired);

    client.on('tokenAboutToExpire', handleTokenAboutToExpire);

    return () => {
      client.removeListener('tokenExpired', handleTokenExpired);

      client.removeListener('tokenAboutToExpire', handleTokenAboutToExpire);
    };
  }, [client, refreshToken, refreshTwilioAccessToken]);

  useEffect(() => {
    if (!client) {
      return;
    }

    const handleMessageAdded = async (message: {
      body: string | null;
      conversation?: {
        uniqueName?: string | null;
      };
    }) => {
      const uniqueName = message.conversation?.uniqueName ?? '';

      if (uniqueName === data?.data.liveUpdatesTopicId) {
        await onMessageAdded(message.body, client);

        return;
      }

      bumpInboxRefreshKey();

      await refreshUnreadCounts();

      if (
        uniqueName === BROADCAST_UPDATES_UNIQUE_NAME ||
        uniqueName.startsWith(SUPPORT_UNIQUE_NAME_PREFIX)
      ) {
        await hydrateChatConversations(client);

        return;
      }

      if (isTradeConversation(uniqueName)) {
        await hydrateArchivedConversations(client);
      }
    };

    client.on('messageAdded', handleMessageAdded);

    return () => {
      client.removeListener('messageAdded', handleMessageAdded);
    };
  }, [
    bumpInboxRefreshKey,
    client,
    data?.data.liveUpdatesTopicId,
    hydrateChatConversations,
    hydrateArchivedConversations,
    onMessageAdded,
  ]);

  useEffect(() => {
    if (!client) {
      return;
    }

    const handleConversationAdded = async () => {
      await hydrateChatConversations(client);

      bumpInboxRefreshKey();
    };

    client.on('conversationAdded', handleConversationAdded);

    return () => {
      client.removeListener('conversationAdded', handleConversationAdded);
    };
  }, [bumpInboxRefreshKey, client, hydrateChatConversations]);

  useEffect(() => {
    updateClient();
  }, [updateClient, accessTokens?.data.twilioAccessToken]);

  useEffect(() => {
    const lockChat = data?.data.systemSettings.lockChat;

    if (typeof lockChat === 'boolean') {
      setLockChat(lockChat);
    }
  }, [data?.data.systemSettings.lockChat, setLockChat]);

  useEffect(clearTwilioAccessTokenIfLoggedOut, [
    clearTwilioAccessTokenIfLoggedOut,
  ]);

  useEffect(tradeInProgressCheck, [tradeInProgressCheck]);

  useEffect(() => resetOnUnmount, [resetOnUnmount]);

  return <>{children}</>;
};

export { TwilioWrapper };
